/**
 * AI Progress Component
 * Displays the current step in the AI generation pipeline
 */

import { Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AiStep, AiMode, AI_STEP_LABELS } from '@/types/ai';
import { Progress } from '@/components/ui/progress';

interface AiProgressProps {
  currentStep: AiStep;
  mode: AiMode;
  bugfixAttempts?: number;
  className?: string;
}

/**
 * Get the steps to display based on mode
 * - Create mode: plan → planApproval → implement → test → bugfix
 * - Modify mode: analyze → plan → planApproval → implement → test → bugfix
 */
function getStepsForMode(mode: AiMode): AiStep[] {
  if (mode === 'create') {
    return ['plan', 'planApproval', 'implement', 'test', 'bugfix'];
  }
  return ['analyze', 'plan', 'planApproval', 'implement', 'test', 'bugfix'];
}

/**
 * Get the status of a step based on current step
 */
function getStepStatus(
  step: AiStep,
  currentStep: AiStep,
  stepsOrder: AiStep[],
): 'pending' | 'active' | 'complete' | 'error' {
  if (currentStep === 'error') {
    // Find the last active step before error
    const currentIndex = stepsOrder.indexOf(step);
    // Mark previous steps as complete, current as error
    if (currentIndex < stepsOrder.length - 1) {
      return 'complete';
    }
    return 'error';
  }

  if (currentStep === 'complete') {
    // All steps except bugfix are complete
    if (step === 'bugfix') return 'pending';
    return 'complete';
  }

  if (currentStep === 'idle') {
    return 'pending';
  }

  const currentIndex = stepsOrder.indexOf(currentStep);
  const stepIndex = stepsOrder.indexOf(step);

  if (stepIndex < currentIndex) return 'complete';
  if (stepIndex === currentIndex) return 'active';
  return 'pending';
}

/**
 * Calculate progress percentage
 */
function calculateProgress(currentStep: AiStep, mode: AiMode): number {
  const steps = getStepsForMode(mode);

  if (currentStep === 'idle') return 0;
  if (currentStep === 'complete') return 100;
  if (currentStep === 'error') return 100;

  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex === -1) return 0;

  // Each step is an equal portion of the progress
  const stepProgress = 100 / steps.length;
  // Show progress as being in the middle of the current step
  return Math.round(currentIndex * stepProgress + stepProgress / 2);
}

export function AiProgress({
  currentStep,
  mode,
  bugfixAttempts = 0,
  className,
}: AiProgressProps) {
  const steps = getStepsForMode(mode);
  const progress = calculateProgress(currentStep, mode);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Step indicators */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const status = getStepStatus(step, currentStep, steps);
          const isLast = index === steps.length - 1;

          // Hide bugfix step unless we're in it or past it
          if (
            step === 'bugfix' &&
            bugfixAttempts === 0 &&
            status === 'pending'
          ) {
            return null;
          }

          // Special handling for planApproval - show as active when waiting for user
          const displayStatus =
            step === 'planApproval' && currentStep === 'planApproval'
              ? 'active'
              : status;

          return (
            <div key={step} className="flex items-center">
              {/* Step circle and label */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors',
                    {
                      'border-muted-foreground/30 bg-background text-muted-foreground':
                        displayStatus === 'pending',
                      'border-primary bg-primary text-primary-foreground':
                        displayStatus === 'active',
                      'border-green-500 bg-green-500 text-white':
                        displayStatus === 'complete',
                      'border-destructive bg-destructive text-destructive-foreground':
                        displayStatus === 'error',
                    },
                  )}
                >
                  {displayStatus === 'complete' && (
                    <Check className="h-4 w-4" />
                  )}
                  {displayStatus === 'error' && <X className="h-4 w-4" />}
                  {displayStatus === 'active' && step !== 'planApproval' && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {displayStatus === 'active' && step === 'planApproval' && (
                    <span className="text-xs font-medium">!</span>
                  )}
                  {displayStatus === 'pending' && (
                    <span className="text-xs font-medium">{index + 1}</span>
                  )}
                </div>
                <span
                  className={cn('mt-1 text-xs', {
                    'text-muted-foreground': displayStatus === 'pending',
                    'font-medium text-foreground': displayStatus === 'active',
                    'text-green-600 dark:text-green-400':
                      displayStatus === 'complete',
                    'text-destructive': displayStatus === 'error',
                  })}
                >
                  {AI_STEP_LABELS[step]}
                  {step === 'bugfix' && bugfixAttempts > 0 && (
                    <span className="ml-1">({bugfixAttempts}/3)</span>
                  )}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div
                  className={cn('mx-2 h-0.5 w-8 transition-colors sm:w-12', {
                    'bg-muted-foreground/30': displayStatus === 'pending',
                    'bg-primary': displayStatus === 'active',
                    'bg-green-500': displayStatus === 'complete',
                  })}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <Progress value={progress} className="h-2" />
    </div>
  );
}
